import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import DefaultLayout from "../../layouts/DefaultLayout";
import { withRouter } from "react-router-dom";
import {
	Button,
	Card,
	Col,
	DatePicker,
	Descriptions,
	Form,
	Input,
	Modal,
	Row,
	Table,
} from "antd";
import { debounce } from "lodash";
import { defaultCurrencyFormat, formRules } from "../../../utilities";
import quoteStatuses from "../../../configs/quote-statuses";
import moment from "moment";
import { CancellationReasonPicker } from "../../ui/forms/NotRenewingReasonPicker";
import { message } from "antd";

@inject("context")
@observer
class CancellationModal extends Component {
	componentDidMount() {
		this.doQuoteCancellationCalculation(new Date());
	}

	formRef = (form) => {
		this.form = form;
	};

	render() {
		const service = this.props.context.policiesService;
		const quote = service.cancellationModalRecord;
		const visible = service.cancellationModalVisible;

		return (
			<Modal
				title="Policy cancellation"
				visible={visible}
				onOk={this.handleOk}
				onCancel={this.handleCancel}
			>
				<Descriptions column={1}>
					<Descriptions.Item label="Policy Number">
						{quote.policyNumber}
					</Descriptions.Item>
					<Descriptions.Item label="Policy Holder">
						{quote.first_name} {quote.last_name}
					</Descriptions.Item>
				</Descriptions>
				<Form
					ref={this.formRef}
					layout="vertical"
					initialValues={{ cancellationDate: moment(new Date()) }}
				>
					<Row>
						<Col>
							<Form.Item
								name="cancellationReason"
								label="Cancellation Reason"
								rules={[formRules.required]}
							>
								<CancellationReasonPicker />
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col>
							<Form.Item
								name="cancellationDate"
								label="Cancellation date"
								rules={[formRules.required]}
							>
								<DatePicker onChange={(e) => this.handleDateChange(e)} />
							</Form.Item>
						</Col>
					</Row>
				</Form>
				<Descriptions column={1}>
					<Descriptions.Item label="Amount Paid">
						{defaultCurrencyFormat(quote.total)}
					</Descriptions.Item>
					<Descriptions.Item label="Adjustment">
						{defaultCurrencyFormat(quote.premiumAdjustment)}
					</Descriptions.Item>
					<Descriptions.Item label="Cancellation fee">
						{defaultCurrencyFormat(quote.cancellationFee)}
					</Descriptions.Item>
					<Descriptions.Item label="Total Due/Refund">
						{defaultCurrencyFormat(quote.totalDueRefund)}
					</Descriptions.Item>
				</Descriptions>
			</Modal>
		);
	}

	handleOk = () => {
		const { policiesService, paymentService } = this.props.context;
		if (
			this.form.getFieldValue("cancellationReason") &&
			this.form.getFieldValue("cancellationDate")
		) {
			policiesService.hideCancellationModal();
			const cancelledPolicy = policiesService.cancellationModalRecord;
			const refundIssued = paymentService.refund(
				cancelledPolicy.quoteId,
				cancelledPolicy.totalDueRefund
			).then(result => { // if refund is issued, cancel the quote if not we display an error message
				if (result) {

					policiesService.cancelQuote(
						this.form.getFieldValue("cancellationReason"),
						this.form.getFieldValue("cancellationDate").toISOString()
					);
					this.props.onModalOk();
				}
			})
				.catch(error => {
					message.error("Error issuing refund: " + error.message);
				});

		}
	};

	handleCancel = async () => {
		const service = this.props.context.policiesService;

		service.hideCancellationModal();
		service.setCancellationModalRecord(null);
	};

	handleDateChange = (e) => {
		const cancellationDate = e;
		this.doQuoteCancellationCalculation(cancellationDate.toISOString());
	};

	doQuoteCancellationCalculation = async (cancellationDate) => {
		const service = this.props.context.policiesService;
		const { policiesService } = this.props.context;

		const { quoteId } = policiesService.cancellationModalRecord;

		await service.calculateQuoteCancellation(cancellationDate, quoteId);
	};
}

/**
 * Shows insurance policies.
 */
@inject("context")
@withRouter
@observer
class Policies extends Component {
	componentDidMount() {
		this.reloadPolicies();
	}

	render() {
		const service = this.props.context.policiesService;

		const columns = [
			{
				title: "Renewal date",
				dataIndex: "renewalDate",
				sorter: true,
				defaultSortOrder: "descend",
				render: (date) => {
					return new Date(date).toLocaleDateString();
				},
			},
			{
				title: "Policy number",
				dataIndex: "policyNumber",
				sorter: true,
			},
			{
				title: "First name",
				dataIndex: "first_name",
				sorter: true,
			},
			{
				title: "Last name",
				dataIndex: "last_name",
				sorter: true,
			},
			{
				title: "Amount Paid",
				dataIndex: "total",
				render: (n) => {
					return defaultCurrencyFormat(n);
				},
			},
			{
				title: "Phone number",
				dataIndex: "phoneNumber",
			},
			{
				title: "Address 1",
				dataIndex: "address1",
			},
			{
				title: "City",
				dataIndex: "city",
			},
			{
				title: "Postcode",
				dataIndex: "postcode",
			},
			{
				title: "Actions",
				render: (field, record) => {
					return (
						<>
							<Button
								type="primary"
								disabled={
									record.status !== quoteStatuses.RENEWALSENT || record.notRenewingReason !== null
								}
								onClick={() => this.handleRenewButtonClick(record)}
							>
								Renew
							</Button>
							<Button
								type="default"
								onClick={() => this.handleUpdateButtonClick(record)}
								disabled={record.status === quoteStatuses.RENEWING}
							>
								Update
							</Button>
							<Button
								type="danger"
								onClick={() => this.handleCancelButtonClick(record)}
								disabled={record.status === quoteStatuses.RENEWING || record.notRenewingReason !== null}
							>
								Cancel
							</Button>
							<Button
								type="default"
								onClick={() => this.handleViewButtonClick(record)}
							>
								View
							</Button>
						</>
					);
				},
			},
		];

		return (
			<DefaultLayout
				layoutContentProps={{
					style: {
						maxWidth: "100%",
						paddingLeft: 40,
						paddingRight: 40,
					},
				}}
			>
				<Row>
					<Col xs={24}>
						<Card title={<h1>Policies</h1>}>
							<Row>
								<Col
									xs={6}
									style={{
										paddingBottom: "15px",
									}}
								>
									<Input.Search
										onSearch={this.handleSearchChange}
										onChange={this.handleSearchChange}
										value={service.search.value}
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24}>
									<Table
										rowKey="quoteId"
										columns={columns}
										dataSource={service.policiesData}
										pagination={service.pagination}
										loading={service.loading}
										onChange={this.handleTableChange}
									/>
									{service.cancellationModalRecord && (
										<CancellationModal onModalOk={this.reloadPolicies} />
									)}
								</Col>
							</Row>
						</Card>
					</Col>
				</Row>
			</DefaultLayout>
		);
	}

	handleTableChange = (pagination, filters, sorter) => {
		const service = this.props.context.policiesService;

		service.setPagination(pagination);
		service.setSorter(sorter);

		service.loadPoliciesData();
	};

	handleSearchChange = (...args) => {
		const value =
			args.length === 2
				? args[1].currentTarget.value
				: args[0].currentTarget.value;

		this.props.context.policiesService.setSearch({
			fields: [
				"policyNumber",
				"organisation.first_name",
				"organisation.last_name",
				"organisation.post_code",
				"organisation.phone",
			],
			value,
		});

		this.debouncedReloadItems();
	};

	handleRenewButtonClick = async (record) => {
		const { policyNumber } = record;

		if (policyNumber) {
			this.props.context.insuranceRenewalsService.setOverThePhone(true);
			this.props.history.push(`/renew/${policyNumber}`);
		}
	};

	handleClaimButtonClick(quote) {
		if (quote && quote.policyNumber) {
			this.props.history.push({
				pathname: "/customer-claim",
				quoteId: quote.quoteId,
			});
		}
	}

	handleUpdateButtonClick = async (record) => {
		const { policyNumber } = record;
		const { policiesService } = this.props.context;
		policiesService.setIsViewMode(false);

		this.props.context.insuranceMtaService.setOverThePhone(true);
		this.props.history.push(`/mta/amendpolicy/${policyNumber}`);

	};

	handleViewButtonClick = (record) => {
		const { policiesService } = this.props.context;
		const { policyNumber } = record;
		policiesService.setIsViewMode(true);
		this.props.context.insuranceMtaService.setOverThePhone(true);
		this.props.history.push(`/mta/amendpolicy/${policyNumber}`);
	};



	handleCancelButtonClick = async (record) => {
		const { policiesService } = this.props.context;
		policiesService.setCancellationModalRecord(record);
		policiesService.showCancellationModal();
	};

	reloadPolicies = async () => {
		await this.props.context.policiesService.loadPoliciesData();
	};

	debouncedReloadItems = debounce(this.reloadPolicies, 250);
}

export default Policies;
